import {useEffect, useState} from 'react'

import {keyBy, omit} from 'es-toolkit'

import {uuid} from '../services/uuid'

import {AttachmentFile, FileId} from '../types'

const useAttachments = (fileSizeLimit: number, enableSrc = false) => {
  const [attachments, setAttachments] = useState<Record<FileId, AttachmentFile>>({})
  const [fileSizeExceededCount, setFileSizeExceededCount] = useState(0)

  const addAttachments = (files: File[], type?: string): FileId[] => {
    const fileNamesExceedingSizeLimit: string[] = []

    const nextFiles = files.filter(file => {
      if (file.size > fileSizeLimit) {
        fileNamesExceedingSizeLimit.push(file.name)
        return false
      }
      return true
    })

    if (!nextFiles.length && !fileNamesExceedingSizeLimit.length) {
      return []
    }

    const uploadingFiles = Array.from(nextFiles).map(file => ({
      file,
      id: uuid(),
      name: file.name,
      size: file.size,
      type,
      src: enableSrc ? URL.createObjectURL(file) : undefined,
    }))

    setAttachments({
      ...attachments,
      ...keyBy(uploadingFiles, file => file.id),
    })

    setFileSizeExceededCount(fileNamesExceedingSizeLimit.length || 0)

    return uploadingFiles.map(({id}) => id)
  }

  const deleteAttachment = (fileId: FileId) => {
    const file = attachments[fileId]

    setAttachments(value => omit(value, [fileId]))
    setFileSizeExceededCount(0)

    if (file.src) {
      URL.revokeObjectURL(file.src)
    }
  }

  const resetAttachments = () => {
    Object.values(attachments).forEach(file => {
      if (file.src) {
        URL.revokeObjectURL(file.src)
      }
    })

    setAttachments({})
    setFileSizeExceededCount(0)
  }

  useEffect(() => {
    return () => {
      // Clean up
      Object.values(attachments).forEach(file => {
        if (file.src) {
          URL.revokeObjectURL(file.src)
        }
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return {
    attachments,
    fileSizeExceededCount,
    addAttachments,
    deleteAttachment,
    resetAttachments,
  }
}

export default useAttachments
